import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import AuthenticatedImage from './AuthenticatedImage';
import {
  buildExecutiveReportModel,
  ExecutiveReportModel,
} from '../utils/executiveReportModel';

interface ProfessionalExecutiveReportProps {
  clientSlug: string;
  clientName: string;
  reportRef: React.RefObject<HTMLDivElement>;
  onReportReady?: () => void;
  onReportDataReady?: (model: ExecutiveReportModel) => void;
  clientLogo?: string;
  apiBase?: string;
}

const ProfessionalExecutiveReport: React.FC<ProfessionalExecutiveReportProps> = ({ 
  clientSlug,
  clientName, 
  reportRef,
  onReportReady,
  onReportDataReady,
  clientLogo,
  apiBase = `${API_URL}/client/me`,
}) => {
  const [reportModel, setReportModel] = useState<ExecutiveReportModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const onReportReadyRef = React.useRef(onReportReady);
  const onReportDataReadyRef = React.useRef(onReportDataReady);

  useEffect(() => {
    onReportReadyRef.current = onReportReady;
  }, [onReportReady]);

  useEffect(() => {
    onReportDataReadyRef.current = onReportDataReady;
  }, [onReportDataReady]);

  // Helper function to convert decimal degrees to DMS format
  const convertToDMS = (decimal: number, isLat: boolean) => {
    const abs = Math.abs(decimal);
    const degrees = Math.floor(abs);
    const minutes = Math.floor((abs - degrees) * 60);
    const seconds = Math.floor(((abs - degrees) * 60 - minutes) * 60);
    const direction = isLat ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W');
    return `${degrees}°${minutes}′${seconds}″ ${direction}`;
  };

  // Helper function to get coordinates for a reserve
  const getCoordinates = (reserveCode: string) => {
    const coords = reportModel?.coordinatesData.find(c => c.name === reserveCode);
    if (coords) {
      return `${convertToDMS(coords.lat, true)}, ${convertToDMS(coords.lon, false)}`;
    }
    return 'Coordinate unavailable';
  };

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('ProfessionalExecutiveReport - Starting data fetch for client:', clientName);
        
        const [volumesResponse, coordinatesResponse] = await Promise.all([
          authenticatedFetch(`${apiBase}/partner-claimed-volumes`),
          authenticatedFetch(`${apiBase}/coordinates`)
        ]);

        console.log('ProfessionalExecutiveReport - Response statuses:', {
          volumes: volumesResponse.status,
          coordinates: coordinatesResponse.status
        });
        if (!volumesResponse.ok || !coordinatesResponse.ok) {
          throw new Error('One or more company-scoped report sources are unavailable');
        }

        const [volumesResult, coordinatesResult] = await Promise.all([
          volumesResponse.json(),
          coordinatesResponse.json(),
        ]);
        if (!volumesResult.volumes_data?.length) {
          throw new Error('No current-season claimed volumes are available');
        }
        const model = buildExecutiveReportModel(
          volumesResult.volumes_data,
          coordinatesResult.coordinates_data || []
        );
        setReportModel(model);
        onReportDataReadyRef.current?.(model);

        console.log('ProfessionalExecutiveReport - Data fetch complete, setting loading to false');
        setTimeout(() => {
          onReportReadyRef.current?.();
        }, 2000);

      } catch (error) {
        console.error('ProfessionalExecutiveReport - Error fetching report data:', error);
        setError(error instanceof Error ? error.message : 'Unable to generate report');
      } finally {
        setLoading(false);
      }
    };

    if (clientName) {
      fetchReportData();
    } else {
      console.error('ProfessionalExecutiveReport - No client name provided');
      setLoading(false);
    }
  }, [apiBase, clientName, clientSlug]);

  if (loading) {
    return (
      <div ref={reportRef} style={{ 
        width: '210mm',
        maxWidth: '794px',
        backgroundColor: '#ffffff',
        padding: '40px',
        textAlign: 'center',
        minHeight: '297mm' // A4 height
      }}>
        <div>Loading report data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div ref={reportRef} style={{ padding: 40, backgroundColor: '#fff', color: '#b91c1c' }}>
        Report unavailable: {error}
      </div>
    );
  }

  if (!reportModel) {
    return (
      <div ref={reportRef} style={{ padding: 40, backgroundColor: '#fff', color: '#b91c1c' }}>
        Report unavailable: no report data was loaded.
      </div>
    );
  }

  const {
    dataByReserve,
    sortedWeeklyTotals,
    uniqueReserves,
  } = reportModel;

  return (
    <div ref={reportRef} style={{ 
      width: '210mm',
      maxWidth: '794px',
      backgroundColor: '#ffffff',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      lineHeight: '1.3',
      color: '#333333',
      margin: '0 auto',
      boxSizing: 'border-box'
    }}>
      
      {/* PAGE 1: Header & Executive Summary */}
      <div style={{
        minHeight: '297mm',
        padding: '20mm',
        pageBreakAfter: 'always',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          paddingBottom: '20px',
          borderBottom: '2px solid #1976d2'
        }}>
        {/* Logo Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <img 
            src="/images/nilus_logo.png" 
            alt="NILUS Logo"
            style={{ height: '60px', objectFit: 'contain' }}
          />
          {clientLogo && (
            <AuthenticatedImage
              src={clientLogo} 
              alt={`${clientName} Logo`}
              style={{ height: '60px', objectFit: 'contain' }}
            />
          )}
        </div>
        
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#1976d2',
          marginBottom: '8px'
        }}>
          Water Replenishment Report
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#666',
          marginBottom: '8px'
        }}>
          Client: {clientName}
        </p>
        <p style={{
          fontSize: '12px',
          color: '#999'
        }}>
          Generated: {new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </p>
      </div>

      {/* Executive Summary - Page 1 */}
      <div style={{ marginBottom: '30px', pageBreakAfter: 'always' }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#1976d2',
          marginBottom: '16px'
        }}>
          The Nilus Project<br/>
          Chile, 2026<br/>
          Executive Summary for Strategic Partner
        </h2>
        
        <p style={{ marginBottom: '16px', lineHeight: '1.4' }}>
          In 2026, the following artificial ice reservoirs (AIR) are included
          in the water replenishment report for {clientName}:
        </p>
        
        {uniqueReserves.map((reserveCode, index) => {
          const coordinates = getCoordinates(reserveCode);
          
          return (
            <p key={index} style={{ marginBottom: '8px', paddingLeft: '16px' }}>
              AIR {reserveCode.replace('icestupa', '')}. Location: {coordinates}
            </p>
          );
        })}
        </div>
      </div>

      {/* PAGE 2: Glossary */}
      <div style={{
        minHeight: '297mm',
        padding: '20mm',
        pageBreakAfter: 'always'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          paddingBottom: '20px',
          borderBottom: '1px solid #1976d2'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#1976d2',
            marginBottom: '8px'
          }}>
            {clientName} - Terminology Reference
          </h2>
          <p style={{ fontSize: '12px', color: '#666' }}>
            Technical definitions for water management metrics
          </p>
        </div>
        
        <h3 style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#1976d2',
          marginBottom: '20px'
        }}>
          Glossary
        </h3>
        <p style={{ marginBottom: '8px', fontSize: '10px' }}>All volume in megalitres (ML):</p>
        
        <div style={{ fontSize: '9px', lineHeight: '1.4' }}>
          <p style={{ marginBottom: '6px' }}>
            <strong>Extracted</strong> – total water taken from the source and used for freezing and infiltration, including losses, minus what has been returned when weather conditions do not allow freezing or infiltration.
          </p>
          <p style={{ marginBottom: '6px' }}>
            <strong>Infiltrated</strong> – water released before the accumulation season that infiltrates into the ground.
          </p>
          <p style={{ marginBottom: '6px' }}>
            <strong>Sprayed</strong> – water dispersed during cold periods, intended to freeze into the ice reservoir.
          </p>
          <p style={{ marginBottom: '6px' }}>
            <strong>Accumulated</strong> – water that has successfully frozen and remains as ice at the end of the accumulation season.
          </p>
          <p style={{ marginBottom: '6px' }}>
            <strong>Lost</strong> – water that evaporated or sublimated during the whole reservoir lifecycle.
          </p>
          <p style={{ marginBottom: '6px' }}>
            <strong>Replenished</strong> – effective contribution: infiltrated water plus accumulated ice that contributes to dry-season runoff or infiltration.
          </p>
        </div>
      </div>

      {/* PAGE 3: Volumes Diagram */}
      <div style={{ 
        minHeight: '297mm',
        padding: '20mm',
        pageBreakAfter: 'always',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <div style={{
          marginBottom: '40px'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#1976d2',
            marginBottom: '8px'
          }}>
            {clientName} - Process Flow Diagram
          </h2>
          <p style={{ fontSize: '12px', color: '#666' }}>
            Visual representation of water management process
          </p>
        </div>
        
        <h3 style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#1976d2',
          marginBottom: '20px'
        }}>
          Volumes Diagram
        </h3>
        <div style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '10px',
          background: '#fafafa'
        }}>
          <img 
            src="/images/data_process_diagram.png" 
            alt="Water Process Flow Diagram"
            style={{
              width: '100%',
              maxWidth: '600px',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>
      </div>

      {/* PAGE 4: Total Replenished Table */}
      <div style={{ 
        minHeight: '297mm',
        padding: '20mm',
        pageBreakAfter: 'always'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
          paddingBottom: '20px',
          borderBottom: '1px solid #1976d2'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#1976d2',
            marginBottom: '8px'
          }}>
            {clientName} - Consolidated Performance
          </h2>
          <p style={{ fontSize: '12px', color: '#666' }}>
            Weekly totals across all claimed artificial ice reservoirs
          </p>
        </div>
        
        <h3 style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#1976d2',
          marginBottom: '20px'
        }}>
          Total Replenished for Client
        </h3>
        
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '9px',
          userSelect: 'text'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Calendar Week</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Week Start Date</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Extracted</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Infiltrated</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Sprayed</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Accumulated</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Lost</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Replenished</th>
            </tr>
          </thead>
          <tbody>
            {sortedWeeklyTotals.map((week: any, index: number) => (
              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>week {week.week_num}</td>
                <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                  {new Date(week.week_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                  {week.extracted.toFixed(1)}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                  {week.infiltrated.toFixed(1)}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                  {week.sprayed.toFixed(1)}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                  {week.accumulated.toFixed(1)}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                  {week.lost.toFixed(1)}
                </td>
                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>
                  {week.replenished.toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Individual AIR Sections */}
      {uniqueReserves.map((reserveCode, reserveIndex) => {
        const reserveData = dataByReserve[reserveCode] || [];
        const coordinates = getCoordinates(reserveCode);
        
        return (
          <div key={reserveIndex} style={{ 
            minHeight: '297mm',
            padding: '20mm',
            pageBreakAfter: reserveIndex < uniqueReserves.length - 1 ? 'always' : 'auto'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '40px',
              paddingBottom: '20px',
              borderBottom: '1px solid #1976d2'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#1976d2',
                marginBottom: '8px'
              }}>
                {clientName} - AIR {reserveCode.replace('icestupa', '')} Detailed Report
              </h2>
              <p style={{ fontSize: '12px', color: '#666' }}>
                Individual performance data for Artificial Ice Reservoir {reserveCode.replace('icestupa', '')}
              </p>
            </div>
            
            <h3 style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1976d2',
              marginBottom: '12px'
            }}>
              Replenished per Artificial Ice Reservoir
            </h3>
            
            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                AIR {reserveCode.replace('icestupa', '')}
              </p>
              <p style={{ marginBottom: '12px', fontSize: '10px', color: '#666' }}>
                Location: {coordinates}
              </p>
            </div>
            
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '10px',
              marginBottom: '30px',
              userSelect: 'text',
              marginTop: '20px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Calendar Week</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Week Start Date</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Extracted</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Infiltrated</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Sprayed</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Accumulated</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Lost</th>
                  <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Replenished</th>
                </tr>
              </thead>
              <tbody>
                {reserveData.map((week: any, index: number) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>week {week.week_num}</td>
                    <td style={{ border: '1px solid #ddd', padding: '4px' }}>
                      {new Date(week.week_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                      {week.extracted?.toFixed(1) || '0.0'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                      {week.infiltrated?.toFixed(1) || '0.0'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                      {week.sprayed?.toFixed(1) || '0.0'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                      {week.accumulated?.toFixed(1) || '0.0'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right' }}>
                      {week.lost?.toFixed(1) || '0.0'}
                    </td>
                    <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>
                      {week.replenished?.toFixed(1) || '0.0'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* FINAL PAGE: Footer */}
      <div style={{
        minHeight: '297mm',
        padding: '20mm',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        fontSize: '12px',
        color: '#666'
      }}>
        <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>
          Partnership Support
        </p>
        <p>
          For strategic inquiries and partnership development:<br/>
          info@nilus.eco • +56 9 8360 5547
        </p>
      </div>
    </div>
  );
};

export default ProfessionalExecutiveReport; 